import { AsYouType, CountryCode } from "libphonenumber-js";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import {
    border,
    colors,
    defaultRadius,
    distances,
    palette,
    placeholderColor,
} from "../../styles/constants";
import { Label } from "../Typography";
import CountryPrefix from "./CountryPrefix";

export enum ValidationState {
    Pristine = "pristine",
    Valid = "valid",
    Invalid = "invalid",
    Warning = "warning",
}

export interface InputValidation {
    state: ValidationState;
    message?: string;
}

export type OnChangeFunction = (event: Event) => void;

interface InputProps {
    label: any;
    phoneNumber: string;
    countryPrefix: string;
    countryPrefixName: string;
    setFieldValue: (field: string, value: string) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    validation?: InputValidation;
    autoComplete?: string;
    maxLength?: number;
    minLength?: number;
    name: string;
    required?: boolean;
    className?: string;
}

const countryCodes: [string, string, string, number | undefined][] = [
    ["+49", "01512 3456789", "DE", undefined],
    ["+48", "512 345 678", "PL", 11],
    ["+47", "406 12 345", "NO", 11], // Can also be formatted XX XX XX XX in some cases
    ["+46", "70-123 45 67", "SE", 13],
    ["+45", "32 12 34 56", "DK", 11],
    ["+44", "07400 123456", "GB", 12],
    ["+358", "041 2345678", "FI", 11],
    ["+354", "611 1234", "IS", 8],
    ["+421", "0912 345 678", "SK", 12],
    ["+43", "0664 123456", "AT", 12],
    ["+32", "0470 12 34 56", "BE", 12],
    ["+385", "091 234 567", "HR", 11],
    ["+357", "96 123456", "CY", 10],
    ["+372", "5123 4567", "EE", 10],
    ["+33", "06 12 34 56 78", "FR", 12],
    ["+49", "0151 23456789", "DE", 13],
    ["+30", "694 123 4567", "GR", 12],
    ["+353", "085 123 4567", "IE", 11],
    ["+39", "320 123 4567", "IT", 12],
    ["+371", "2123 4567", "LV", 10],
    ["+370", "612 34567", "LT", 10],
    ["+352", "621 123 456", "LU", 11],
    ["+356", "9696 1234", "MT", 10],
    ["+31", "06 12345678", "NL", 11],
    ["+351", "912 345 678", "PT", 11],
    ["+386", "031 234 567", "SI", 10],
    ["+34", "612 34 56 78", "ES", 11],
];

const countryCodePrefixes: string[] = countryCodes.map(
    ([prefix, placeholder]) => prefix,
);

interface StringByString {
    [key: string]: string;
}

const codeByPrefix: StringByString = countryCodes.reduce(
    (reduced, [prefix, placeholder, countryCode]) => {
        return {
            ...reduced,
            [prefix]: countryCode,
        };
    },
    {},
);

const maxLengthByPrefix: { [key: string]: number | undefined } =
    countryCodes.reduce(
        (reduced, [prefix, placeholder, countryCode, maxLength]) => {
            return {
                ...reduced,
                [prefix]: maxLength,
            };
        },
        {},
    );

const PhoneNumber = ({
    label,
    countryPrefix,
    countryPrefixName,
    phoneNumber,
    name,
    autoComplete,
    disabled,
    validation,
    setFieldValue,
    onBlur,
    required,
    className,
}: InputProps) => {
    const { t } = useTranslation();
    return (
        <Wrapper className={className}>
            <Label htmlFor="phone_number">
                {label} {required ? "" : t("common.optional")}
            </Label>
            <Inputs>
                <CountryPrefix
                    value={countryPrefix}
                    options={countryCodePrefixes}
                    onChange={(value) => {
                        setFieldValue(countryPrefixName, value);
                    }}
                    disabled={disabled}
                />
                <InputElement
                    value={phoneNumber}
                    id={name}
                    type="tel"
                    autoComplete={autoComplete || "tel-national"}
                    name={name}
                    disabled={disabled}
                    placeholder={""}
                    onChange={(event: any) => {
                        const value = event.currentTarget.value.trim();
                        const position = event.currentTarget.selectionStart;
                        const wasLastPosition = position === value.length;
                        const phoneFormatter = new AsYouType(
                            codeByPrefix[countryPrefix] as CountryCode,
                        );
                        const formattedNumber = phoneFormatter.input(value);
                        event.currentTarget.value = formattedNumber;
                        if (wasLastPosition) {
                            event.currentTarget.setSelectionRange(
                                formattedNumber.length,
                                formattedNumber.length,
                            );
                        } else {
                            event.currentTarget.setSelectionRange(
                                position,
                                position,
                            );
                        }
                        setFieldValue(name, formattedNumber);
                    }}
                    onBlur={onBlur}
                    className={validation ? validation.state : ""}
                    maxLength={maxLengthByPrefix[countryPrefix]}
                    required={required}
                />
            </Inputs>
            {validation && validation.message && (
                <Message htmlFor={name} className={validation.state}>
                    {validation.message}
                </Message>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`;

const Inputs = styled.div`
    display: flex;
    margin-bottom: ${distances.normal};
`;

const InputElement = styled.input`
    box-sizing: border-box;
    border: none;
    outline: none;
    border: ${border.normal} solid ${palette.neutral[400]};
    border-radius: ${defaultRadius};
    background: ${colors.background};
    padding: ${distances.tiny} ${distances.small12};
    text-align: left;
    width: 100%;
    -moz-appearance: textfield;
    font-size: 14px;
    min-height: 40px;
    font-feature-settings: 'tnum';
    transition: all 150ms cubic-bezier(0.2, 0, 0.2, 1);

    &::-webkit-inner-spin-button {
        display: none;
        -webkit-appearance: none;
    }

    &::placeholder {
        color: ${placeholderColor};
        opacity: 1;
    }

    &:focus {
        outline: none;
        border: ${border.normal} solid ${palette.primary[300]};
        box-shadow: 0 0 0 3px ${palette.primary[100]};

    }
    &.invalid,
    &:focus .invalid {
        border: ${border.normal} solid ${palette.destructive[300]};
    }

    &.invalid:focus {
        box-shadow: 0 0 0 3px ${palette.destructive[100]};
    }

    &.valid,
    &:focus .valid {
        border: ${border.normal} solid ${palette.success[300]};
    }

    &.valid:focus {
        box-shadow: 0 0 0 3px ${palette.success[100]};
    }

    &:disabled {
        background: ${palette.neutral[50]};
        border: 1px solid ${palette.neutral[200]};
        color: ${palette.neutral[500]};
    }
`;

const Message = styled.label`
    position: absolute;
    top: 72px;
    left: calc(68px + 40px);
    font-size: 14px;
    line-height: ${distances.small};

    &.invalid {
        color: ${colors.invalid};
    }

    &.valid {
        color: ${colors.valid};
    }
`;

export default PhoneNumber;
